/**
 * TaskTimeline — fetches `GET /api/artifacts/:artifactId/timeline` and renders
 * the merged event / rule-run / agent-run / decision history via the shared
 * `Timeline` app component.
 *
 * The request goes through Refine's configured API URL (`useApiUrl`) using the
 * browser `fetch` with `credentials: 'include'` so the worker session cookie is
 * sent. This is the only sanctioned direct fetch (a custom dashboard-style
 * endpoint, per frontend.spec §27).
 */
import * as React from "react";
import { useApiUrl } from "@refinedev/core";
import type { TimelineEntry } from "@aos/shared";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Timeline } from "@/components/app/Timeline";

import { LoadingState, ErrorState, EmptyState } from "../projects/_shared";

interface TimelineResponse {
  items: TimelineEntry[];
}

function useArtifactTimeline(artifactId: string | null | undefined) {
  const apiUrl = useApiUrl();
  const [state, setState] = React.useState<{
    items: TimelineEntry[];
    loading: boolean;
    error: string | null;
  }>({ items: [], loading: Boolean(artifactId), error: null });

  React.useEffect(() => {
    if (!artifactId) {
      setState({ items: [], loading: false, error: null });
      return;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));

    fetch(`${apiUrl}/artifacts/${encodeURIComponent(artifactId)}/timeline`, {
      headers: { Accept: "application/json" },
      credentials: "include",
    })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(`Timeline request failed (${res.status})`);
        }
        return (await res.json()) as TimelineResponse;
      })
      .then((body) => {
        if (cancelled) return;
        setState({
          items: Array.isArray(body.items) ? body.items : [],
          loading: false,
          error: null,
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          items: [],
          loading: false,
          error:
            err instanceof Error ? err.message : "Failed to load timeline.",
        });
      });

    return () => {
      cancelled = true;
    };
  }, [apiUrl, artifactId]);

  return state;
}

export function TaskTimeline({
  artifactId,
  flush = false,
}: {
  artifactId: string | null | undefined;
  /**
   * Render the timeline E0 (no Card) for use inside an Inspector section, which
   * supplies its own "Timeline" heading. Default `false` keeps the standalone
   * carded panel (the Artifact show page's bottom panel).
   */
  flush?: boolean;
}) {
  const { items, loading, error } = useArtifactTimeline(artifactId);

  const body = loading ? (
    <LoadingState label="Loading timeline…" />
  ) : error ? (
    <ErrorState error={{ message: error }} />
  ) : items.length === 0 ? (
    <EmptyState message="No activity yet for this artifact." />
  ) : (
    <Timeline items={items} />
  );

  if (flush) {
    return body;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Timeline</CardTitle>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
}

export default TaskTimeline;
